import { useEffect } from "react";

/**
 * Grows a textarea's height to fit its content as the user types. Collapses to 0 before measuring on
 * every `value` change, so shrinking (deleting a line) is picked up too, not just growth.
 *
 * @param textAreaRef - the textarea element to resize, or null before it mounts.
 * @param value - the textarea's current value; height is recomputed whenever this changes.
 * @param maxHeight - optional cap in px. Once the content would exceed it, height stops growing at the
 *   cap and the field scrolls internally instead (its own CSS must set `overflow-y: auto`) — e.g. the
 *   chat composer's 6-line cap (design-spec.md §2 screen 07). Omitted, height grows unbounded as before.
 */
const useAutosizeTextArea = (textAreaRef: HTMLTextAreaElement | null, value: string, maxHeight?: number) => {
  useEffect(() => {
    if (textAreaRef) {
      textAreaRef.style.height = "0px";
      const nextHeight = textAreaRef.scrollHeight + 2;
      textAreaRef.style.height = (maxHeight ? Math.min(nextHeight, maxHeight) : nextHeight) + "px";
    }
  }, [textAreaRef, value, maxHeight]);
};

export default useAutosizeTextArea;
